/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";
import { CREATE_ORDER } from "../graphql/createOrder.mutation";
import { StorageModule } from "@vlinder-web/storage-module-react";
import { REGISTER_PAYMENT } from "../graphql/registerPayment.mutation";

const is = require("is_js");

export interface ICreateOrderArgs {
  email: string;
  name: string;
  payOrder: string;
  price: any;
  currency: string;
  code?: string;
  platformFee?: number;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface ICreateOrderTypeMethods extends IPluginMethodMap {
  createOrder(args: any): Promise<any>;
}

export class CreateOrderService implements IAgentPlugin {
  readonly methods: ICreateOrderTypeMethods;

  constructor() {
    this.methods = {
      createOrder: this.createOrder.bind(this),
    };
  }

  async createOrder(args: ICreateOrderArgs) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const storageService = StorageModule.getInstance();

    let variable: any = {
      data: {
        name: args?.name,
        email: args?.email,
        payOrder: args?.payOrder,
        price: args?.price,
        currency: args?.currency,
        // TEMP: Platform Fee disabled until backend deployment
        platformFee: args?.platformFee
          ? parseFloat(args?.platformFee as any)
          : 0,
      },
    };

    if (args?.code) {
      variable = { ...variable, discountCode: args?.code };
    }

    try {
      if (!client) {
        throw new Error("GraphQL client not found");
      }

      const provider = storageService.get(
        "currencyExchangeLocal",
        "local",
      )?.provider;

      console.log("payment provider", provider);
      console.log("createOrder variables", variable);

      if (provider === "CAL" || provider === "CALKIDS") {
        let variables: any = {
          data: {
            currency: args?.currency,
            amount: args?.price,
            payorder: args?.payOrder,
          },
        };

        if (args?.code) {
          variables = {
            ...variables,
            data: {
              ...variables?.data,
              discountCode: args?.code,
            },
          };
        }

        const resCal = await client.mutate({
          mutation: REGISTER_PAYMENT,
          variables,
          fetchPolicy: "no-cache",
        });

        if (is.existy(resCal?.data?.registerPayment)) {
          const paymentUrl =
            resCal?.data?.registerPayment?.data?.session?.[
              "payment-hosted-url"
            ];

          if (paymentUrl) {
            window.location.href = paymentUrl;

            return {
              ...resCal?.data?.registerPayment,
              redirection: true,
            };
          }

          return {
            ...resCal?.data?.registerPayment,
            redirection: false,
          };
        }

        if (args?.code && Number(args?.price) === 0) {
          return true;
        }

        throw new Error("Some Error Occurred");
      }

      const res = await client.mutate({
        mutation: CREATE_ORDER,
        variables: variable,
        fetchPolicy: "no-cache",
      });

      const paymentRes = res?.data?.getAnglPayRedirectURL;

      console.log("getAnglPayRedirectURL response", paymentRes);

      if (is.existy(paymentRes?.redirectUrl)) {
        window.location.href = paymentRes?.redirectUrl;
        return paymentRes;
      }

      if (is.existy(paymentRes?.sessionId?.sessionId)) {
        return {
          redirect: "apv2",
          sessionId: paymentRes?.sessionId?.sessionId,
          data: {
            name: args?.name,
            email: args?.email,
            price: args?.price,
            currency: args?.currency,
            payOrder: args?.payOrder,
            platformFee: args?.platformFee
              ? parseFloat(args?.platformFee as any)
              : 0,
          },
        };
      }

      if (
        is.existy(paymentRes?.token) ||
        is.existy(paymentRes?.captureContext) ||
        is.existy(paymentRes?.sessionId?.token)
      ) {
        const token =
          paymentRes?.token ||
          paymentRes?.captureContext ||
          paymentRes?.sessionId?.token;
        const sessionId = paymentRes?.sessionId;
        return {
          redirect: "cybersource",
          token,
          sessionId,
          data: {
            name: args?.name,
            email: args?.email,
            price: args?.price,
            currency: args?.currency,
            payOrder: args?.payOrder,
            platformFee: args?.platformFee
              ? parseFloat(args?.platformFee as any)
              : 0,
          },
        };
      }

      if (args?.code) {
        return true;
      }

      throw new Error("Some Error Occurred");
    } catch (err: any) {
      const _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);

      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }
}
